import { random, reduce } from "lodash";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { UserData } from "@/entities/users/types";
import { dropdownPropsType } from "@/components/controls/Dropdown/Dropdown";
import {
  FactorAnalysisDataType,
  factorType
} from "@/components/layouts/FactorAnalysis";
import { dealsDrilldownTableDataType } from "@/components/layouts/DealsDrilldownModal/DealsDrilldownModal";
import { Column } from "@/pages/deals/components/KanbanBoard/KanbanBoard";
import {
  CsmRenewalConfidenceLevel,
  DealData,
  DealMilestone,
  DealStage
} from "@/pages/deals/types";
import { FormField } from "@/components/controls/Form/Form";
import { PersonData } from "@/entities/people/types";

export const factorAnalysisData: FactorAnalysisDataType = [
  {
    label: "Amanda Rivera",
    borderColor: "#867CF8"
  },
  {
    label: "Brandon Scott",
    borderColor: "#42B0A6"
  },
  {
    label: "Ryan Thomas",
    borderColor: "#EC9D16"
  },
  {
    label: "Johana Smith",
    borderColor: "#4B83EB"
  }
];

export const dropdownProps: dropdownPropsType = {
  label: "Timeframe",
  options: {
    allTimes: "All times",
    month: "This month",
    quarter: "This quarter",
    year: "This year"
  },
  defaultValue: "allTimes"
};

export const stagesDropdownProps: dropdownPropsType = {
  label: "Timeframe",
  options: {
    allTimes: "All times",
    ytd: "YTD",
    last12Months: "Last 12 Months",
    last24Months: "Last 24 Months"
  },
  defaultValue: "allTimes"
};

export const percentageComparisonChartProps: {
  name: string;
  value: number;
}[] = [
  {
    name: "you",
    value: 3
  },
  {
    name: "team",
    value: 2
  },
  {
    name: "organization",
    value: 4
  },
  {
    name: "industry",
    value: 3
  }
];

export const emptyUserData: UserData = {
  id: "",
  firstName: "",
  lastName: "",
  displayName: "",
  department: 1,
  email: "",
  lastLoginAt: "",
  timezone: "",
  role: 1,
  jobTitle: "",
  avatarUrl: "https://i.pravatar.cc/" + (random(50) + 1).toString(),
  groupIds: []
};

const insights = {
  0: [
    "For deals value",
    "$100K AND time spent on POC between 2 and 5 weeks",
    "Time to close is shorter by 5%"
  ],
  1: [
    "When time spent on POC",
    "6 weeks AND success criteria include a transaction conversion lift KPI",
    "Time to close is shorter by 7.5%"
  ]
};

const recommendations = [
  {
    company: "Walmart",
    teamMemberName: "Amanda Rivera",
    description:
      "For the Walmart deal, make sure to keep your POC time spent between 2 and 5 weeks."
  },
  {
    company: "Quickmart",
    teamMemberName: "Brandon Scott",
    description:
      "Add transaction conversion lift success criteria to the deal with Quickmart."
  }
];

export const factors: factorType[] = [
  {
    id: "winRate",
    title: "Win rate",
    description:
      "The percentage of deals that the sales team successfully closed as wins. The calculation is (Number of Deals Won / Total Number of Deals) * 100."
  },
  {
    id: "leadConversion",
    title: "Lead conversion",
    description:
      "How effectively your sales team turns leads into deals. The calculation is (Number of Leads Converted to Deals/ Total Number of Leads) * 100."
  },
  {
    id: "closeDealTime",
    title: "Time to close a deal",
    description:
      "The time it takes for a deal from the time it was created and until it was closed won. It is also known as sales cycle length.",
    impactingFactors: [
      {
        id: "POCTime",
        title: "Time spent on POC",
        impactLevel: "high",
        insights,
        recommendations
      },
      {
        id: "stakeholdersNumber",
        title: "Number of stakeholders",
        impactLevel: "high",
        insights,
        recommendations
      },
      {
        id: "successCriteriaNumber",
        title: "Number of success criteria",
        impactLevel: "medium",
        insights,
        recommendations
      },
      {
        id: "followUpEmailsNumber",
        title: "Number of follow up emails",
        impactLevel: "medium",
        insights,
        recommendations
      }
    ]
  },
  {
    id: "technicalEvaluationTime",
    title: "Time spent on Technical Evaluation",
    description: "The time a deal spends on the Technical Evaluation milestone."
  },
  {
    id: "qualificationTime",
    title: "Time spent on Qualification",
    description: "The time a deal spends on the Qualification milestone."
  },
  {
    id: "conversionOfStage",
    title: "Conversion from one stage to the following one",
    description:
      "The percentage of deals that are successfully moving from stage A to stage B out of the total deals on stage A."
  },
  {
    id: "conversionOfMilestone",
    title: "Conversion from one Sales Milestone to another",
    description:
      "The percentage of deals that are successfully moving from milestone A to milestone B out of the total deals on milestone A."
  }
];

export const dealsModalMockData: dealsDrilldownTableDataType = [
  {
    displayName: "Trader joe's",
    status: 2,
    stage: "problemStatement",
    stakeholdersScore: 41,
    confidenceLevel: 1,
    closeDate: "06/11/2024",
    mrr: 1520
  },
  {
    displayName: "Rami Levi",
    status: 1,
    stage: "POC",
    stakeholdersScore: 52,
    confidenceLevel: 2,
    closeDate: "04/08/2024",
    mrr: 3444
  },
  {
    displayName: "AM-PM",
    status: 1,
    stage: "pilotAcceptance",
    stakeholdersScore: 64,
    confidenceLevel: 3,
    closeDate: "04/08/2024",
    mrr: 35620
  },
  {
    displayName: "Tiv Ta'am",
    status: 2,
    stage: "goodFit",
    stakeholdersScore: 81,
    confidenceLevel: 1,
    closeDate: "04/22/2024",
    mrr: 11320
  },
  {
    displayName: "Costco",
    status: 1,
    stage: "POC",
    stakeholdersScore: 92,
    confidenceLevel: 2,
    closeDate: "04/08/2024",
    mrr: 21890
  },
  {
    displayName: "Walmart",
    status: 1,
    stage: "goodFit",
    stakeholdersScore: 15,
    confidenceLevel: 1,
    closeDate: "04/08/2024",
    mrr: 11320
  },
  {
    displayName: "Whole foods",
    status: 2,
    stage: "pilotAcceptance",
    stakeholdersScore: 21,
    confidenceLevel: 4,
    closeDate: "04/19/2024",
    mrr: 8620
  }
];

export const initialKanbanBoardColumns: Column[] = [
  {
    id: 1,
    title: "Good fit?",
    milestone: DealMilestone.QUALIFICATION,
    items: []
  },
  {
    id: 2,
    title: "Problem statement",
    milestone: DealMilestone.PPP,
    items: []
  },
  {
    id: 5,
    title: "POC",
    milestone: DealMilestone.TECHNICAL_EVALUATION,
    items: []
  },
  {
    id: 6,
    title: "Pilot",
    milestone: DealMilestone.TECHNICAL_EVALUATION,
    items: []
  },
  {
    id: 8,
    title: "Pilot acceptance",
    milestone: DealMilestone.EXECUTIVE_SIGNOFF,
    items: []
  }
];


export const peopleMockData: PersonData[] = [
  {
    firstName: "Tony",
    lastName: "Ko",
    companyId: "c-sla-d6bed0ab",
    jobTitle: "Practice Area Lead",
    phone: null,
    email: "tonyk@slalom.com",
    emails: ["tonyk@slalom.com"],
    id: "p-tonk-fce3e0e9",
    createdAt: "2015-02-18T18:16:42Z"
  },
  {
    firstName: "Mohammad",
    lastName: "Rasool",
    companyId: "c-sav-ccfe03fc",
    jobTitle: "Director, Network Infrastructure and Security",
    phone: "(209) 404-2417 bad # zoom",
    email: "mrasool@savemart.com",
    emails: ["mrasool@savemart.com"],
    id: "p-mohr-39ff25e5",
    createdAt: "2015-02-20T17:24:34Z"
  },
  {
    firstName: "Scott",
    lastName: "Johnson",
    companyId: "c-lev-2b7595fe",
    jobTitle: null,
    phone: null,
    email: "sjohnson7@levi.com",
    emails: ["sjohnson7@levi.com"],
    id: "p-scoj-23d8ef38",
    createdAt: "2015-02-20T21:47:36Z"
  }
];

export const dealsMockData: DealData[] = [
  {
    id: "fbMarketing",
    name: "Facebook - Marketing",
    companyId: "c-acme-1234",
    arr: 1070,
    mrr: null,
    arrLocal: null,
    mrrLocal: null,
    status: 1,
    stage: 1,
    aeConfidenceLevel: 2,
    managedBy: "u-johs-5678",
    contactId: "C1",
    expectedCloseAt: null,
    dealValueAmount: null,
    probability: null,
    openForDays: null,
    techEvaluationStatus: null,
    qualificationScore: null,
    aiAssignedProbability: null,
    lastActivity: null,
    typeOfLastActivity: null,
    createdAt: null,
    daysInStage: 17
  },
  {
    id: "fbDM",
    name: "Facebook - DM",
    companyId: "c-acme-1234",
    arr: 2080,
    mrr: null,
    arrLocal: null,
    mrrLocal: null,
    status: 2,
    stage: 1,
    aeConfidenceLevel: 3,
    managedBy: "u-johs-5678",
    contactId: "C2",
    expectedCloseAt: null,
    dealValueAmount: null,
    probability: null,
    openForDays: null,
    techEvaluationStatus: null,
    qualificationScore: null,
    aiAssignedProbability: null,
    lastActivity: null,
    typeOfLastActivity: null,
    createdAt: null,
    daysInStage: 30
  }
];

export const dealStagesMockData: DealStage[] = [
  { id: 1, name: "Prospecting", stageOrder: 1, milestone: 1 },
  { id: 2, name: "Demo", stageOrder: 2, milestone: 1 },
  { id: 3, name: "Demo to Decision Makers", stageOrder: 3, milestone: 2 },
  { id: 4, name: "Waiting Data POC", stageOrder: 4, milestone: 2 },
  { id: 5, name: "POC", stageOrder: 5, milestone: 2 },
  { id: 6, name: "Pilot", stageOrder: 6, milestone: 3 },
  { id: 7, name: "Proposal", stageOrder: 7, milestone: 4 },
  { id: 8, name: "Negotiation/Review", stageOrder: 8, milestone: 4 }
];

export const companiesMockData = [
  {
    id: "c-acme-1234",
    name: "Acme Technologies Inc",
    status: 1,
    sdrOwner: "john.smith@company.com",
    aeOwner: "sarah.jones@company.com",
    industryId: 2,
    industryName: "Education",
    subIndustryId: null,
    subIndustryName: null,
    city: "San Francisco",
    stateRegion: "California",
    postalCode: "94105",
    numOfEmployees: 250,
    createdAt: null,
    parentCompany: null
  }
];

export const usersMockData = [
  { ...emptyUserData, id: "u-johs-5678", name: "John Smith" }
];

export const formFields: FormField[] = [
  {
    name: "name",
    label: "Name",
    type: "text",
    required: true
  },
  {
    name: "numOfEmployees",
    label: "Number of employees",
    type: "number",
    required: false
  }
];

const DEMO_DEAL_ID = "d-cla-3b352d1e";
const DEMO_LEAD_ID = "l-aaro-1ca776cc";

export const useIsDemoMode = () => useFeatureFlag(FeatureFlags.CRM_6987_DEMO_MODE).enabled;
export const useIsDemoDeal = (dealId: string) => useIsDemoMode() && dealId === DEMO_DEAL_ID;
export const useIsDemoLead = (leadId: string) => useIsDemoMode() && leadId === DEMO_LEAD_ID;

// Demo-only AI renewal probability. Spread over the three confidence levels by
// hashing the contract id rather than calling random(), so a contract keeps the
// same value across re-renders, navigation and quick view — a value that
// reshuffles mid-demo reads as a bug on stage.
export const mockAiRenewalProbability = (
  contractId: string | undefined | null
): CsmRenewalConfidenceLevel => {
  const levels = [
    CsmRenewalConfidenceLevel.LOW,
    CsmRenewalConfidenceLevel.MEDIUM,
    CsmRenewalConfidenceLevel.HIGH
  ];
  const hash = reduce(
    contractId || "",
    (accumulator, char) => (accumulator * 31 + char.charCodeAt(0)) % 997,
    7
  );
  return levels[hash % levels.length];
};

